import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  useToast,
  Box,
  Text,
  Progress,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";

const LabelModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [labelName, setLabelName] = useState("");
  const [labelDescription, setLabelDescription] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");

  const uploadFile = async (file, path) => {
    try {
      const fileExt = file.name.split(".").pop()?.toLowerCase() || path;
      const timestamp = new Date().getTime();
      const fileName = `label_${timestamp}.${fileExt}`;
      const filePath = `${path}/${fileName}`;

      setUploadStatus(`"${file.name}" 파일 업로드 중...`);

      const { data, error: urlError } = await supabase.storage
        .from("image")
        .createSignedUploadUrl(filePath);

      if (urlError || !data?.signedUrl) {
        console.error("URL 발급 오류:", urlError);
        throw new Error("Signed URL 발급 실패");
      }

      const signedUrl = data.signedUrl;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signedUrl, true);
        xhr.setRequestHeader("Content-Type", file.type || "image/jpeg");

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            console.error("업로드 실패 상세:", {
              status: xhr.status,
              statusText: xhr.statusText,
              response: xhr.response,
            });
            reject(new Error(`업로드 실패: ${xhr.status} ${xhr.statusText}`));
          }
        };
        xhr.onerror = () => {
          console.error("네트워크 오류 상세:", xhr);
          reject(new Error("네트워크 오류가 발생했습니다."));
        };
        xhr.send(file);
      });

      const { data: publicUrlData } = supabase.storage
        .from("image")
        .getPublicUrl(filePath);

      return publicUrlData?.publicUrl;
    } catch (error) {
      console.error("파일 업로드 중 오류 발생:", error);
      throw error;
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext !== "jpg" && ext !== "jpeg") {
        toast({
          title: "이미지 파일 형식 오류",
          description: "JPG 파일만 업로드할 수 있습니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        e.target.value = "";
        return;
      }
      setLogoFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!labelName.trim()) {
      toast({
        title: "레이블 이름을 입력해주세요.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      let logoUrl = null;

      // 로고 이미지가 있는 경우 Storage에 업로드
      if (logoFile) {
        const ext = logoFile.name.split(".").pop().toLowerCase();
        if (ext !== "jpg" && ext !== "jpeg") {
          toast({
            title: "이미지 파일 형식 오류",
            description: "JPG 파일만 업로드할 수 있습니다.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setIsLoading(false);
          return;
        }
        setUploadStatus("로고 이미지 업로드 중...");
        logoUrl = await uploadFile(logoFile, `label/${user.email}`);
      }

      // 레이블 데이터 저장
      const { error: insertError } = await supabase.from("label").insert({
        name: labelName,
        description: labelDescription,
        logo: logoUrl,
        user: user.email,
      });

      if (insertError) {
        throw insertError;
      }

      toast({
        title: "레이블이 성공적으로 등록되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // 입력 필드 초기화
      setLabelName("");
      setLabelDescription("");
      setLogoFile(null);

      // 모달 닫기
      onClose();

      // 성공 후 콜백 실행 (데이터 새로고침)
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error creating label:", error);
      toast({
        title: "레이블 등록 중 오류가 발생했습니다.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
      setUploadStatus("");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent maxW="600px" borderRadius="xl" boxShadow="xl">
        <ModalHeader
          bg="blue.50"
          borderTopRadius="xl"
          py={4}
          fontSize="xl"
          fontWeight="bold"
          color="blue.700"
        >
          레이블 생성
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={6}>
          <VStack spacing={6}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium" color="gray.700">
                레이블 이름
              </FormLabel>
              <Input
                value={labelName}
                onChange={(e) => setLabelName(e.target.value)}
                placeholder="레이블 이름을 입력하세요"
                borderRadius="md"
                focusBorderColor="blue.400"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="medium" color="gray.700">
                레이블 소개
              </FormLabel>
              <Textarea
                value={labelDescription}
                onChange={(e) => setLabelDescription(e.target.value)}
                placeholder="레이블 소개를 입력하세요"
                rows={4}
                borderRadius="md"
                focusBorderColor="blue.400"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="medium" color="gray.700">
                레이블 로고
              </FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                borderRadius="md"
                p={1}
              />
            </FormControl>

            {isLoading && (
              <Box width="100%" p={4} bg="blue.50" borderRadius="lg">
                <Text mb={2} fontWeight="medium" color="blue.700">
                  {uploadStatus}
                </Text>
                <Progress
                  value={uploadProgress}
                  size="sm"
                  colorScheme="blue"
                  borderRadius="full"
                  hasStripe
                  isAnimated
                />
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter p={4} borderTopWidth={1} borderColor="gray.200">
          <Button variant="ghost" mr={3} onClick={onClose}>
            취소
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="등록 중..."
          >
            등록
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LabelModal;
